import { useMemo } from 'react';
import { FormField, Select } from '@amzn/awsui-components-react-v3';
import { ActivityData } from '../../../../interfaces/activity';
import { ActivityAudienceItemData } from '../../../../../common/interfaces/businessDataItem/activityAudienceItem';
import {
    FORM_ERROR_SELECTOR,
    getOptionsAndLookupForSelectInputPolarisV3,
    getStatusType,
} from '../../../../../imt/components/Instructor/FormSections/FormSections.common';
import { useFilteredAudiences } from '../../../../../common/hooks/useFilteredAudiences';

interface AudienceListSelectProps {
    handleFieldEvent: (values: Partial<ActivityData>) => void;
    formValues: Pick<
        Partial<ActivityData>,
        'activity_audience' | 'activity_modality' | 'program'
    >;
    errors: {
        [
            key in keyof Pick<
                Partial<ActivityData>,
                'activity_audience' | 'activity_modality' | 'program'
            >
        ]: string;
    };
}

const AudienceListSelect = ({
    formValues,
    handleFieldEvent,
    errors,
}: AudienceListSelectProps) => {
    const { filteredAudiences, isLoading, isLoaded, error } =
        useFilteredAudiences(formValues.program);

    const {
        valueLookup: activityAudienceLookup,
        valueOptions: activityAudienceOptions,
    } = useMemo(() => {
        return getOptionsAndLookupForSelectInputPolarisV3<ActivityAudienceItemData>(
            filteredAudiences,
            (activityAudience: ActivityAudienceItemData) => ({
                label: activityAudience.activity_audience,
                value: activityAudience.pk as string,
            }),
        );
    }, [filteredAudiences]);

    return (
        <FormField errorText={errors?.activity_audience} label="Audience">
            <Select
                className={errors?.activity_audience && FORM_ERROR_SELECTOR}
                placeholder={
                    isLoading ? 'Loading audiences' : 'Select an audience'
                }
                options={activityAudienceOptions}
                selectedOption={
                    activityAudienceLookup[formValues.activity_audience]
                }
                onChange={(e) =>
                    handleFieldEvent({
                        activity_audience: e.detail.selectedOption.label,
                    })
                }
                loadingText="Loading audiences"
                errorText="An error occurred while loading audiences"
                empty="No audiences found"
                statusType={getStatusType(isLoading, isLoaded, error)}
                disabled={!isLoaded}
                data-testid={`EditBaseDetailsActivityAudience`}
            />
        </FormField>
    );
};

export default AudienceListSelect;
